import { Map, Compass, User, CalendarDays, Bell } from "lucide-react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useBadges } from "@/hooks/use-badges";
import { useUnreadNotificationCount } from "@/hooks/use-notifications";
import { HoneyBalance } from "@/components/HoneyBalance";
import { NotificationNudge } from "@/components/NotificationNudge";

type NavTab = 'map' | 'nearby' | 'events' | 'notifications' | 'profile';

interface NavigationProps {
  active?: NavTab;
  onMapClick?: () => void;
  onNearbyClick?: () => void;
  onAccountClick?: () => void;
  onNotificationsClick?: () => void;
  onEventsClick?: () => void;
}

export function Navigation({ active = 'map', onMapClick, onNearbyClick, onAccountClick, onNotificationsClick, onEventsClick }: NavigationProps) {
  const [, setLocation] = useLocation();
  const { user } = useAuth();

  const { friendsBadge, profileBadge } = useBadges();
  const totalBadge = friendsBadge + profileBadge;
  const badgeText = totalBadge > 9 ? "9+" : String(totalBadge);

  const unreadNotifications = useUnreadNotificationCount();
  const notifBadgeText = unreadNotifications > 9 ? "9+" : String(unreadNotifications);

  const activeBtnCls = "bg-primary text-primary-foreground shadow-lg shadow-primary/25";
  const idleBtnCls = "text-zinc-400 hover:text-white hover:bg-white/5";

  return (
    <div className="flex flex-col items-center gap-2">
      {user && <NotificationNudge />}
      <nav className="flex scale-90 origin-bottom">
        <div className="flex items-center gap-1 p-2 rounded-full glass-card border-white/10">
          <button
            data-testid="nav-map"
            onClick={() => { onMapClick?.(); setLocation("/"); }}
            className={`p-3 rounded-full transition-all duration-300 ${active === 'map' ? activeBtnCls : idleBtnCls}`}
          >
            <Map className="w-5 h-5" />
          </button>
          
          <button
            data-testid="nav-nearby"
            onClick={() => onNearbyClick?.()}
            className={`p-3 rounded-full transition-all duration-300 ${active === 'nearby' ? activeBtnCls : idleBtnCls}`}
          >
            <Compass className="w-5 h-5" />
          </button>

          <button
            data-testid="nav-events"
            onClick={() => onEventsClick?.()}
            className={`p-3 rounded-full transition-all duration-300 ${active === 'events' ? activeBtnCls : idleBtnCls}`}
          >
            <CalendarDays className="w-5 h-5" />
          </button>

          <button
            data-testid="nav-notifications"
            onClick={() => onNotificationsClick?.()}
            className={`relative p-3 rounded-full transition-all duration-300 ${active === 'notifications' ? activeBtnCls : idleBtnCls}`}
          >
            <Bell className="w-5 h-5" />
            {user && unreadNotifications > 0 && (
              <span
                data-testid="badge-notifications"
                className="absolute top-0.5 right-0.5 min-w-4 h-4 px-1 rounded-full bg-red-500 text-white text-xs font-semibold flex items-center justify-center pointer-events-none"
              >
                {notifBadgeText}
              </span>
            )}
          </button>

          <button
            data-testid="nav-account"
            onClick={() => {
              if (user?.role === "business") {
                setLocation("/business");
              } else {
                onAccountClick?.();
              }
            }}
            className={`relative p-3 rounded-full transition-all duration-300 ${active === 'profile' ? activeBtnCls : idleBtnCls}`}
          >
            <User className="w-5 h-5" />
            {totalBadge > 0 && (
              <span
                data-testid="badge-account"
                className="absolute top-0.5 right-0.5 min-w-4 h-4 px-1 rounded-full bg-red-500 text-white text-xs font-semibold flex items-center justify-center pointer-events-none"
              >
                {badgeText}
              </span>
            )}
          </button>

          {user && (
            <>
              <div className="w-px h-5 bg-white/10" />
              <HoneyBalance
                showLabel={false}
                onClick={() => {
                  if (user.role === "business") {
                    setLocation("/business");
                  } else {
                    onAccountClick?.();
                  }
                }}
              />
            </>
          )}
        </div>
      </nav>
    </div>
  );
}
